import { formatScale } from '../../lib/calibration';
import { useEditorLayoutStore } from '../../store/editorLayoutStore';
import { useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import type { BottomPanelTab } from '../../types/editorLayout';
import RunSimulationButton from '../simulation/RunSimulationButton';
import GateControlPanel from '../simulation/GateControlPanel';
import ScenarioControlPanel from '../simulation/ScenarioControlPanel';
import SimulationPlaybackControls from '../simulation/SimulationPlaybackControls';
import SimulationResultsPanel from '../simulation/SimulationResultsPanel';
import SimulationDiagnosticsPanel from '../simulation/SimulationDiagnosticsPanel';
import ProjectDiagnosticsPanel from './ProjectDiagnosticsPanel';

const TABS: { id: BottomPanelTab; label: string }[] = [
  { id: 'scenario', label: 'Scenario' },
  { id: 'gates', label: 'Gates' },
  { id: 'results', label: 'Results' },
  { id: 'diagnostics', label: 'Diagnostics' },
];

function BottomPanelBar() {
  const document = useEditorStore((s) => s.document);
  const projectId = useEditorStore((s) => s.projectId);
  const permission = useEditorStore((s) => s.permission);
  const routeSourceId = useEditorStore((s) => s.routeSourceId);
  const calibrationMode = useEditorStore((s) => s.calibrationMode);
  const snapToGrid = useEditorStore((s) => s.snapToGrid);
  const runStatus = useSimulationStore((s) => s.run.status);
  const runMode = useSimulationStore((s) => s.run.runMode);
  const setBottomPanelOpen = useSimulationStore((s) => s.setBottomPanelOpen);
  const setBottomPanelTab = useSimulationStore((s) => s.setBottomPanelTab);
  const setLayoutBottomOpen = useEditorLayoutStore((s) => s.setBottomOpen);
  const setLayoutBottomTab = useEditorLayoutStore((s) => s.setBottomTab);

  const canRun = permission === 'run' || permission === 'edit';
  const nodeCount = document?.nodes.length ?? 0;
  const routeCount = document?.routes.length ?? 0;
  const unit = document?.settings?.unit_system ?? 'metric_m';
  const scaleLabel = document?.settings?.pixels_per_meter
    ? formatScale(document.settings.pixels_per_meter, unit)
    : null;

  const openPanel = (tab: BottomPanelTab = 'scenario') => {
    setLayoutBottomOpen(true);
    setLayoutBottomTab(tab);
    setBottomPanelOpen(true);
    setBottomPanelTab(tab);
  };

  return (
    <footer className="flex shrink-0 items-center justify-between gap-3 border-t-2 border-accent bg-surface-raised px-4 py-1.5 text-xs text-text-subtle shadow-brand">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <button
          type="button"
          className="badge-accent-on-dark rounded-md px-2 py-1 text-xs font-medium hover:bg-accent/30"
          onClick={() => openPanel('scenario')}
        >
          ▲ Simulation
        </button>
        <button
          type="button"
          className="hidden text-text-muted hover:text-text sm:inline"
          onClick={() => openPanel('scenario')}
        >
          Configure scenario &amp; gates
        </button>
        <span className="text-border">|</span>
        <span>
          <strong className="font-medium text-text">{nodeCount}</strong> nodes
        </span>
        <span className="text-border">|</span>
        <span>
          <strong className="font-medium text-text">{routeCount}</strong> routes
        </span>
        {runStatus === 'running' && runMode === 'realtime' && (
          <>
            <span className="text-border">|</span>
            <SimulationPlaybackControls compact />
          </>
        )}
        {runStatus === 'running' && runMode !== 'realtime' && (
          <>
            <span className="text-border">|</span>
            <span className="font-medium text-accent-on-dark">Calculating…</span>
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {canRun && (
          <RunSimulationButton
            projectId={projectId}
            size="compact"
            className="hidden sm:inline-flex"
          />
        )}
        {snapToGrid && (
          <span className="hidden text-text-subtle sm:inline">Snap on</span>
        )}
        {routeSourceId && (
          <span className="text-accent-on-dark">Route mode active</span>
        )}
        {calibrationMode && (
          <span className="text-accent-on-dark">Calibration mode</span>
        )}
        {scaleLabel && (
          <span className="hidden text-text-subtle md:inline">{scaleLabel}</span>
        )}
      </div>
    </footer>
  );
}

function BottomPanelDocked() {
  const permission = useEditorStore((s) => s.permission);
  const document = useEditorStore((s) => s.document);
  const projectId = useEditorStore((s) => s.projectId);
  const routeSourceId = useEditorStore((s) => s.routeSourceId);
  const calibrationMode = useEditorStore((s) => s.calibrationMode);
  const snapToGrid = useEditorStore((s) => s.snapToGrid);
  const runStatus = useSimulationStore((s) => s.run.status);
  const runMode = useSimulationStore((s) => s.run.runMode);
  const bottomPanelTab = useEditorLayoutStore((s) => s.layout.bottomTab);
  const setBottomPanelOpen = useSimulationStore((s) => s.setBottomPanelOpen);
  const setBottomPanelTab = useSimulationStore((s) => s.setBottomPanelTab);
  const setLayoutBottomOpen = useEditorLayoutStore((s) => s.setBottomOpen);
  const setLayoutBottomTab = useEditorLayoutStore((s) => s.setBottomTab);

  const readOnly = permission === 'view';
  const canRun = permission === 'run' || permission === 'edit';
  const nodeCount = document?.nodes.length ?? 0;
  const routeCount = document?.routes.length ?? 0;
  const unit = document?.settings?.unit_system ?? 'metric_m';
  const scaleLabel = document?.settings?.pixels_per_meter
    ? formatScale(document.settings.pixels_per_meter, unit)
    : null;

  const closePanel = () => {
    setLayoutBottomOpen(false);
    setBottomPanelOpen(false);
  };

  const selectTab = (tab: BottomPanelTab) => {
    setLayoutBottomTab(tab);
    setBottomPanelTab(tab);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-raised">
      <footer className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-1.5 text-xs text-text-subtle">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-ghost rounded-md px-2 py-1 text-xs font-medium"
            onClick={closePanel}
          >
            ▼ Simulation
          </button>
          <span className="text-border">|</span>
          <span>
            <strong className="font-medium text-text">{nodeCount}</strong> nodes
          </span>
          <span className="text-border">|</span>
          <span>
            <strong className="font-medium text-text">{routeCount}</strong> routes
          </span>
          {runStatus === 'running' && runMode === 'realtime' && (
            <>
              <span className="text-border">|</span>
              <SimulationPlaybackControls compact />
            </>
          )}
          {runStatus === 'running' && runMode !== 'realtime' && (
            <>
              <span className="text-border">|</span>
              <span className="font-medium text-accent-on-dark">Calculating…</span>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {canRun && (
            <RunSimulationButton projectId={projectId} size="compact" />
          )}
          {snapToGrid && (
            <span className="hidden text-text-subtle sm:inline">Snap on</span>
          )}
          {routeSourceId && (
            <span className="text-accent-on-dark">Route mode active</span>
          )}
          {calibrationMode && (
            <span className="text-accent-on-dark">Calibration mode</span>
          )}
          {scaleLabel && (
            <span className="hidden text-text-subtle md:inline">{scaleLabel}</span>
          )}
        </div>
      </footer>
      <div className="flex shrink-0 gap-1 border-b border-border px-2 pt-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`rounded-t-md px-3 py-1.5 text-xs font-medium transition ${
              bottomPanelTab === tab.id
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
            onClick={() => selectTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'results' && runStatus === 'completed' && (
              <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
            )}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {bottomPanelTab === 'scenario' && (
          <ScenarioControlPanel readOnly={readOnly || !canRun} />
        )}
        {bottomPanelTab === 'gates' && (
          <GateControlPanel readOnly={readOnly} />
        )}
        {bottomPanelTab === 'results' && <SimulationResultsPanel />}
        {bottomPanelTab === 'diagnostics' && (
          <div className="space-y-4">
            <SimulationDiagnosticsPanel />
            <div className="border-t border-border">
              <div className="px-4 pt-3 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
                Project validation
              </div>
              <ProjectDiagnosticsPanel readOnly={readOnly} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BottomPanel({ docked = false }: { docked?: boolean }) {
  if (docked) return <BottomPanelDocked />;
  return <BottomPanelBar />;
}
